/**
 * The app shell: load the registry, hold the query, show the answer.
 *
 * Nothing about which specs exist, which can be filtered, how finely they step
 * or what they are called is written down here. It all arrives from
 * `/attributes` at runtime, so adding an attribute to the registry is an INSERT
 * and this bundle never changes (REQ-DM-002, REQ-WEB-001, REQ-WEB-020).
 *
 * If you find yourself about to type a lens spec's name into this file, that is
 * the bug.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "preact/hooks";

import type { FilterOptions, Attribute, LensPage, LensRow, Range, Registry } from "./api";
import { ApiProblem, api, problemText } from "./api";
import type { MountList } from "./api";
import * as Cam from "./camera";
import type { Camera } from "./camera";
import {
  ActiveFilterSummary,
  activeFilterLabels,
  CameraBar,
  ColumnPanel,
  Disclosure,
  FilterPanel,
  SmallerCoverageOptIn,
  SortControl,
  useDebounced,
} from "./controls";
import { LegendNote } from "./agreement";
import { ComputedPanel } from "./builder";
import { LensDetail } from "./detail";
import { formatCount } from "./format";
import type { Query } from "./query";
import { MAX_COLUMNS, parseQuery, requestColumns, shortLabel, toSearch } from "./query";
import { setComputedLabels } from "./formula";
import { EmptyResult, ResultCards, ResultTable } from "./results";
import { Compare } from "./compare";
import { fetchAll, saveCsv, toCsv } from "./csv";
import { freshnessLine } from "./freshness";
import { LandingHandle, LandingPanel, useLanding } from "./landing";
import { Disclaimer, SiteFooter, SiteLinks } from "./pages";
import {
  disableBrowserScrollRestoration,
  isCompare,
  lensSlug,
  stashedScroll,
  useRoute,
} from "./router";
import { setFixedPageMeta } from "./meta";

const PAGE_SIZE = 25;

/**
 * The pages of the last list view, so Back restores all of them.
 *
 * REQ-WEB-023 says returning from a detail page puts the user back where they
 * were, and REQ-WEB-030 extends that over paging: someone who loaded four
 * pages and opened the last result returns to four pages, not to the first 25
 * with a scroll position that no longer exists.
 *
 * It is held here rather than in the URL because paging position is not view
 * state (D7) — a shared link opens the query, not someone's place in it — and
 * it is read only on a return, never to answer a fresh query, so it is a
 * restoration buffer rather than a response cache that could serve stale rows.
 */
let restorable: { key: string; pages: LensPage[] } | null = null;

type Panel = "filters" | "columns" | "computed" | null;

export function App() {
  const [registry, setRegistry] = useState<Registry | null>(null);
  const [ranges, setRanges] = useState<Map<string, Range>>(new Map());
  const [mountData, setMountData] = useState<MountList | null>(null);
  // REQ-WEB-035 (#207): the brand and image-circle choices for this camera.
  // Null while loading or if it fails, and the panel then falls back to the
  // site-wide lists rather than offering nothing.
  const [scopedOptions, setScopedOptions] = useState<FilterOptions | null>(null);
  const [bootError, setBootError] = useState<string | null>(null);
  // The URL wins over the memory: a camera named in the URL always beats the
  // remembered one, or a shared link would show different results per person.
  const [camera, setCamera] = useState<Camera>(
    () => Cam.fromSearch(location.search) ?? Cam.remembered() ?? Cam.ALL,
  );

  const [query, setQuery] = useState<Query>(() => parseQuery(location.search, null));
  const [panel, setPanel] = useState<Panel>(null);
  // Which attributes have a filter control on screen. Distinct from which
  // filters are *set*: you open a control before you constrain anything.
  const [filterKeys, setFilterKeys] = useState<string[]>([]);

  // Every page fetched for the current query, oldest first (REQ-WEB-030).
  // The list is a paged view of the whole result set, so the rows accumulate;
  // only a change of query starts them over.
  const [pages, setPages] = useState<LensPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // REQ-WEB-022: specs a link asked for beyond the limit, shown until dismissed.
  const arrivedSearch = useRef(location.search);
  const [columnsNotice, setColumnsNotice] = useState<{ asked: number; shown: number } | null>(null);
  // REQ-FX-011: parts of a link that could not be honoured and were left out.
  const [ignoredNotice, setIgnoredNotice] = useState<string[] | null>(null);
  // Bumped by "Try again" to re-run the search for the same query (REQ-WEB-013).
  const [attempt, setAttempt] = useState(0);
  const [moreError, setMoreError] = useState<string | null>(null);
  const [wide, setWide] = useState(() => window.matchMedia("(min-width: 860px)").matches);

  const { route, navigate, replace, written } = useRoute();
  const slug = lensSlug(route.path);
  const comparing = isCompare(route.path);

  // The tab title follows the view (REQ-WEB-038). A lens names itself once it
  // has loaded (detail.tsx); until then the previous title stands.
  useEffect(() => {
    if (comparing) setFixedPageMeta("/compare");
    else if (!slug) setFixedPageMeta("/");
  }, [comparing, slug]);

  // The exact query string the visible pages were fetched with. `loadMore`
  // needs it verbatim — a cursor is only valid for the sort it was issued for
  // (REQ-API-006) — and it is the key a late response is checked against.
  const sentQuery = useRef("");
  // `loadMore` fires from a click, so it needs the pages as they are *now*
  // rather than as they were when its dependencies last changed.
  const pagesRef = useRef<LensPage[]>([]);
  const moreRef = useRef<HTMLButtonElement>(null);
  // Set when the list effect steps aside for a detail or compare route, so the
  // next run knows it is a *return* rather than a new query. Only a return may
  // read the cache, which keeps this from becoming general response caching.
  const cameFromDetail = useRef(false);

  // Which lenses are ticked. In the URL so a comparison is shareable and so
  // Back from it restores the selection (REQ-WEB-027, REQ-WEB-002).
  const [selected, setSelected] = useState<string[]>(() => {
    const raw = new URLSearchParams(location.search).get("selected");
    return raw ? raw.split(",").filter(Boolean) : [];
  });
  const toggleSelect = useCallback((s: string) => {
    setSelected((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));
  }, []);
  // The browser gives up on restoring scroll before this list has finished
  // fetching, so REQ-WEB-023's "return to where you were" is done by hand.
  useEffect(disableBrowserScrollRestoration, []);

  // The footer's "Updated ..." line (REQ-WEB-044). If the endpoint fails the
  // line is simply absent: a footer note is never worth an error banner.
  const [freshness, setFreshness] = useState<string | null>(null);
  useEffect(() => {
    const ac = new AbortController();
    api.freshness(ac.signal)
      .then((f) => setFreshness(freshnessLine(f, Date.now())))
      .catch(() => setFreshness(null));
    return () => ac.abort();
  }, []);

  // ---- boot: registry and ranges, in parallel ----
  useEffect(() => {
    const ac = new AbortController();
    Promise.all([
      api.registry(ac.signal), api.ranges(ac.signal), api.mounts(ac.signal),
      api.yearRanges(ac.signal),
    ])
      .then(([reg, rng, mts, yrs]) => {
        setRegistry(reg);
        setRanges(new Map([...rng.ranges, ...yrs.ranges].map((r) => [r.key, r])));
        setMountData(mts);
        // Re-parse now that the registry can validate keys: a stale link naming
        // an attribute that no longer exists should degrade, not break.
        // The link as it arrived, not location.search: the first render already
        // parsed it without the registry and wrote the trimmed columns back to
        // the address, so re-reading the address would hide what the link
        // asked for (REQ-WEB-022).
        const parsed = parseQuery(arrivedSearch.current, reg);
        setQuery(parsed);
        setColumnsNotice(
          parsed.columnsDropped
            ? { asked: parsed.columns.length + parsed.columnsDropped, shown: parsed.columns.length }
            : null,
        );
        setIgnoredNotice(parsed.ignored ?? null);
        setFilterKeys([...new Set(parsed.filters.map((f) => f.key))]);
      })
      .catch((e: unknown) => {
        if (ac.signal.aborted) return;
        setBootError(e instanceof Error ? e.message : "Could not load the spec list.");
        setLoading(false);
      });
    return () => ac.abort();
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 860px)");
    const on = () => setWide(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);

  // ---- the query drives both the URL and the request ----
  const search = useMemo(
    () => toSearch(query, {
      ...Cam.toParams(camera),
      ...(selected.length ? { selected: selected.join(",") } : {}),
    }),
    [query, camera, selected],
  );
  const debounced = useDebounced(search, 180);

  // ---- which changes get a history entry of their own (REQ-WEB-023, D33) ----
  // A handler says what kind of change it is about to make; the effect below
  // sees the URL change and decides whether it starts a new entry or joins the
  // one already made. Slider moves and keystrokes after the first say nothing,
  // so they replace the current entry.
  const intent = useRef<"search" | "mount" | "filter" | null>(null);
  const episode = useRef<{ kind: "search" | "filter"; timer: ReturnType<typeof setTimeout> } | null>(null);
  const endEpisode = useCallback(() => {
    if (episode.current) clearTimeout(episode.current.timer);
    episode.current = null;
  }, []);
  useEffect(() => endEpisode, [endEpisode]);
  const announce = useCallback(
    (kind: "search" | "mount" | "filter") => {
      intent.current = kind;
      // A change that leaves the URL as it was must not mark the next one.
      setTimeout(() => { if (intent.current === kind) intent.current = null; }, 500);
      if (kind === "mount") {
        endEpisode();
        return;
      }
      // Choices within ~2 s of each other are one change; typing is one change
      // until it pauses for ~1 s.
      if (episode.current?.kind === kind) {
        clearTimeout(episode.current.timer);
        episode.current.timer = setTimeout(endEpisode, kind === "search" ? 1000 : 2000);
      }
    },
    [endEpisode],
  );
  const changeFilters = useCallback(
    (q: Query | ((cur: Query) => Query), how?: "slide") => {
      if (how !== "slide") announce("filter");
      setQuery(q);
    },
    [announce],
  );

  useEffect(() => {
    if (slug) return; // the detail route owns the URL while it is open
    const kind = intent.current;
    intent.current = null;
    let push = false;
    if (kind === "mount") {
      push = true;
    } else if (kind && episode.current?.kind !== kind) {
      push = true;
      endEpisode();
      episode.current = {
        kind,
        timer: setTimeout(endEpisode, kind === "search" ? 1000 : 2000),
      };
    }
    // Every change is handed over, including one that happens to match the URL
    // as it stands: history writes are coalesced now (see HISTORY_MIN_GAP_MS),
    // so a change back to the address bar's own value still has to cancel the
    // newer one waiting to be written. `replace` is what decides there is
    // nothing to do.
    replace(`${location.pathname}?${search}`, push);
  }, [search, slug, replace, endEpisode]);

  // Coming back from a detail page, or a back/forward press: the query lives in
  // the URL and is re-read here, so filters, sort and columns return with it
  // (REQ-WEB-002).
  //
  // ONLY when the URL changed from OUTSIDE. The effect above writes the URL from
  // the query, and this one wrote the query back from the URL, so every change
  // round-tripped through a string. Drag faster than that loop settles and this
  // effect fires holding an older `route.search`, writing a stale query over the
  // newer one — the control jumps back while a request for the newer value is
  // still in flight.
  //
  // Owner, 2026-09-12: "multiple small movements makes it search and then
  // refresh. Multiple changed while it's searching seems to make it misbehave."
  //
  // "Ours" is what the router actually WROTE, not what this component last
  // asked it to write. Those two parted company when history writes started
  // being coalesced: a queued write lands carrying an older query than the one
  // asked for most recently, and matching on the request would fail to
  // recognise it and read it straight back over the newer value.
  useEffect(() => {
    if (slug || !registry) return;
    if (written.current !== null && written.current === location.pathname + route.search) {
      // Our own write coming back round. Nothing external happened.
      return;
    }
    written.current = null;
    const parsed = parseQuery(route.search, registry);
    setQuery(parsed);
    // Raise the notice, never clear it here: the page corrects the address to
    // the specs it kept, and reading that corrected address back must not
    // withdraw the explanation of why it changed. "OK" clears it.
    if (parsed.columnsDropped) {
      setColumnsNotice({
        asked: parsed.columns.length + parsed.columnsDropped,
        shown: parsed.columns.length,
      });
    }
    if (parsed.ignored) setIgnoredNotice(parsed.ignored);
    const fromUrl = Cam.fromSearch(route.search);
    if (fromUrl) setCamera(fromUrl);
    const sel = new URLSearchParams(route.search).get("selected");
    setSelected(sel ? sel.split(",").filter(Boolean) : []);
  }, [route.search, slug, registry, written]);

  useEffect(() => Cam.remember(camera), [camera]);

  // REQ-WEB-035: a lens opened from the list carries its camera in the link, and
  // a lens (or the comparison) judges its category on the camera the URL names.
  // From the URL only, never the remembered one: a pasted lens link must show
  // every visitor the same page (rule 1 in camera.ts).
  const lensSearch = Cam.lensSearch(camera);
  // The same function across appends, so a card already on screen is not
  // rebuilt just because the list grew (REQ-WEB-018).
  const openLens = useCallback(
    (s: string) => navigate(`/lens/${s}${lensSearch}`),
    [navigate, lensSearch],
  );
  const urlFormat = (() => {
    const c = Cam.fromSearch(route.search);
    return c ? Cam.formatOf(c) : null;
  })();

  useEffect(() => {
    const ac = new AbortController();
    setScopedOptions(null);
    api.filterOptions(Cam.toFilters(camera), ac.signal)
      .then(setScopedOptions)
      .catch(() => { /* the site-wide lists stand in */ });
    return () => ac.abort();
  }, [camera.mount, camera.format, camera.includeSmaller]);

  useEffect(() => {
    if (!registry) return;
    if (slug || comparing) {
      cameFromDetail.current = true;
      return;
    }
    const returning = cameFromDetail.current;
    cameFromDetail.current = false;
    const q = toSearch(
      { ...query, columns: requestColumns(query, camera.includeSmaller) },
      { page_size: String(PAGE_SIZE), ...Cam.toRequest(camera) },
    );
    const previous = sentQuery.current;
    sentQuery.current = q;
    setMoreError(null);

    // The trigger is the whole URL, which carries things the API never sees —
    // the selection, most of all. Ticking a result for comparison must not
    // throw away the pages the user loaded to find it and drop them somewhere
    // else in the list (REQ-WEB-027, REQ-WEB-030).
    if (q === previous && pagesRef.current.length) {
      setLoading(false);
      return;
    }

    // Coming back to the same query with pages beyond the first: put them all
    // back rather than re-fetching page one, which would strand the scroll
    // position REQ-WEB-023 is about to restore.
    if (returning && restorable?.key === q && restorable.pages.length > 1) {
      setPages(restorable.pages);
      setError(null);
      setLoading(false);
      return;
    }

    const ac = new AbortController();
    setLoading(true);
    setError(null);
    api
      .lenses(q, ac.signal)
      .then((p) => {
        setPages([p]);
        restorable = { key: q, pages: [p] };
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (ac.signal.aborted) return;
        // The rows on screen answered the previous question. Left up under the
        // error, they read as results for the controls the user just changed.
        setPages([]);
        restorable = null;
        setError(
          e instanceof ApiProblem
            ? problemText(e.problem)
            : e instanceof Error
              ? e.message
              : "Something went wrong.",
        );
        setLoading(false);
      });
    return () => ac.abort();
    // `debounced` is the trigger; `query` is read fresh so a drag sends one
    // request with the final value rather than one per frame.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced, registry, slug, comparing, attempt]);

  /**
   * Fetch the next page and append it (REQ-WEB-030).
   *
   * Explicit, never scroll-triggered: the page has one scroll context and a
   * footer that has to stay reachable (REQ-WEB-019). Nothing here touches the
   * URL or the router, so paging adds no history entry and a shared link still
   * opens the query rather than a position inside it.
   */
  const loadMore = useCallback(() => {
    const last = pagesRef.current[pagesRef.current.length - 1];
    if (!last?.next_cursor || loadingMore) return;
    // Disabling the button while it loads is what stops a triple-tap becoming
    // three pages — but disabling the focused element hands focus to <body>,
    // so a keyboard user's next Tab restarts from the top of the page and has
    // to walk every loaded row to get back here (REQ-WEB-009).
    const refocus = document.activeElement === moreRef.current;
    const key = sentQuery.current;
    setLoadingMore(true);
    setMoreError(null);
    api
      .lenses(`${key}&cursor=${encodeURIComponent(last.next_cursor)}`)
      .then((p) => {
        // A query change while this was in flight makes the response answer a
        // question nobody is asking any more.
        if (sentQuery.current !== key) return;
        setPages((prev) => {
          // Guard against two appends racing onto the same cursor: only the
          // one still following the page it was issued from may land.
          if (prev[prev.length - 1] !== last) return prev;
          const next = [...prev, p];
          restorable = { key, pages: next };
          return next;
        });
        setLoadingMore(false);
        if (refocus) queueMicrotask(() => moreRef.current?.focus());
      })
      .catch((e: unknown) => {
        if (sentQuery.current !== key) return;
        setMoreError(
          e instanceof ApiProblem
            ? problemText(e.problem)
            : e instanceof Error
              ? e.message
              : "Could not load more lenses.",
        );
        setLoadingMore(false);
        if (refocus) queueMicrotask(() => moreRef.current?.focus());
      });
  }, [loadingMore]);

  useEffect(() => {
    pagesRef.current = pages;
  }, [pages]);

  // Restore the scroll position stashed on this history entry when we left it.
  // Runs after the rows are in the DOM, or there is nothing to scroll to.
  useEffect(() => {
    if (slug || comparing || loading) return;
    const y = stashedScroll();
    if (y === null || y === 0) return;
    requestAnimationFrame(() => scrollTo(0, y));
  }, [slug, comparing, loading]);

  /**
   * Does this row cover less than the chosen sensor? (REQ-WEB-026)
   *
   * Only meaningful while the opt-in is on — otherwise such lenses are not in
   * the results at all. The comparison is the coverage *rank* from `/mounts`,
   * never a rule about which formats subset which, which the requirement
   * forbids the frontend from holding.
   */
  const cropsBelow = useMemo(() => {
    if (!camera.includeSmaller || !camera.format || !mountData) return undefined;
    const rank = new Map(mountData.coverages.map((c) => [c.slug, c.rank]));
    const mine = rank.get(camera.format);
    if (mine === undefined) return undefined;
    return (row: LensRow): boolean => {
      const cov = row.values["coverage"]?.value;
      if (typeof cov !== "string") return false;
      const r = rank.get(cov);
      return r !== undefined && r < mine;
    };
  }, [camera.includeSmaller, camera.format, mountData]);

  const byKey = useMemo(
    () => new Map<string, Attribute>((registry?.attributes ?? []).map((a) => [a.key, a])),
    [registry],
  );

  // #218: a computed column is labelled from its formula, not its name. Set
  // before anything below renders a label.
  useMemo(
    () => setComputedLabels(query.computed, (k) => shortLabel(byKey.get(k), k)),
    [query.computed, byKey],
  );

  // REQ-WEB-036. Held here so the panel and its masthead handle agree.
  const [landingCollapsed, setLandingCollapsed] = useLanding();

  const clearFilters = useCallback(() => {
    changeFilters((q) => ({ ...q, filters: [] }));
    setFilterKeys([]);
  }, [changeFilters]);

  // REQ-WEB-011: the whole query, not the rows paged in (D7). The same request
  // the list makes, at the API's page-size ceiling, followed to the end.
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const exportView = useCallback(async () => {
    setExporting(true);
    setExportError(null);
    try {
      const q = toSearch(
        { ...query, columns: requestColumns(query, camera.includeSmaller) },
        { page_size: "200", ...Cam.toRequest(camera) },
      );
      const rows = await fetchAll(q, (s) => api.lenses(s));
      const now = new Date();
      saveCsv(
        toCsv(rows, query.columns, byKey, location.href, now),
        `lens-specs-${now.toISOString().slice(0, 10)}.csv`,
      );
    } catch (e) {
      setExportError(e instanceof Error ? e.message : "The export could not be made.");
    } finally {
      setExporting(false);
    }
  }, [query, camera, byKey]);

  if (bootError) {
    return (
      <main class="shell">
        <div class="empty">
          <p class="empty-title">The spec list could not be loaded.</p>
          <p class="empty-body">{bootError}</p>
          <button type="button" class="button" onClick={() => location.reload()}>
            Try again
          </button>
        </div>
      </main>
    );
  }

  if (!registry) {
    return (
      <main class="shell">
        <p class="loading" role="status">
          Loading…
        </p>
      </main>
    );
  }

  // Detail and compare have no footer of their own, and REQ-WEB-016 wants the
  // licence reachable from every page, not only from the list.
  if (comparing) {
    return (
      <>
        <Compare
          slugs={selected}
          registry={byKey}
          mounts={mountData}
          onBack={() => (history.length > 1 ? history.back() : navigate("/"))}
          onRemove={toggleSelect}
          format={urlFormat}
          lensSearch={lensSearch}
          onOpen={(s) => navigate(`/lens/${s}${lensSearch}`)}
        />
        <SiteFooter />
      </>
    );
  }

  if (slug) {
    return (
      <>
        <LensDetail
          slug={slug}
          format={urlFormat}
          coverages={mountData?.coverages}
          registry={byKey}
          onBack={() => (history.length > 1 ? history.back() : navigate("/"))}
        />
        <SiteFooter />
      </>
    );
  }

  const rows = useMemo(() => pages.flatMap((p) => p.items), [pages]);
  // REQ-WEB-031 as amended by D26 (#211): when the list spans more than one
  // mount, each result names its mount, so the same lens on Sony E and on
  // Nikon Z can be told apart. Decided from the rows themselves, not the camera
  // picker, so a filter that leaves one mount under "All mounts" repeats nothing.
  const mountOf = useMemo(() => {
    if (new Set(rows.map((r) => r.mount)).size < 2) return undefined;
    const names = new Map((mountData?.mounts ?? []).map((m) => [m.slug, m.name]));
    return (r: { mount: string }) => names.get(r.mount) ?? r.mount;
  }, [rows, mountData]);
  const lastPage = pages[pages.length - 1] ?? null;
  // Every page reports the same total; the first is as good as any.
  const total = pages[0]?.total ?? null;
  const remaining = total === null ? 0 : Math.max(0, total - rows.length);
  const activeCount = new Set(query.filters.map((f) => f.key)).size;
  // D29: the button says what is active. One filter is named ("Weight ≤ 500 g"),
  // because on a phone the chips that would otherwise say so are not shown.
  const activeLabels = registry ? activeFilterLabels(registry, query) : [];
  const filterDetail =
    activeLabels.length === 1 ? activeLabels[0] : activeCount ? `${activeCount} active` : undefined;

  /**
   * What the result count says (REQ-WEB-019, REQ-WEB-030).
   *
   * Three states, because a paged list has to be honest about which one it is
   * in: still partial, complete after paging, or small enough that paging
   * never came up.
   */
  const resultCount = (() => {
    const shown = total ?? rows.length;
    const noun = shown === 1 ? "lens" : "lenses";
    const matching = activeCount ? " matching" : "";
    if (total !== null && rows.length < total)
      return `${formatCount(rows.length)} of ${formatCount(total)} ${noun}${matching}`;
    if (total !== null && total > PAGE_SIZE)
      return `All ${formatCount(total)} ${noun}${matching} shown`;
    return `${formatCount(shown)} ${noun}${activeCount ? " match" : ""}`;
  })();
  const selectedSet = new Set(selected);

  return (
    <div class="shell">
      <header class="masthead">
        <h1 class="wordmark">
          lens<span class="wordmark-dot">·</span>specs
        </h1>
        <p class="tagline">Every figure cited. Start from what you need.</p>
        {/* REQ-WEB-037: always visible, above the camera. In the masthead row
            rather than a row of its own: a separate row pushed the phone's
            first result to 300-311 px, past the top-third limit (REQ-WEB-019). */}
        <div class="search">
          <label class="visually-hidden" for="search-input">Search lenses by maker or model name</label>
          <input
            id="search-input"
            class="search-input"
            type="search"
            inputMode="search"
            autoComplete="off"
            spellcheck={false}
            placeholder="Search, e.g. RF 50"
            value={query.q}
            onInput={(e) => {
              announce("search");
              setQuery((cur) => ({ ...cur, q: (e.target as HTMLInputElement).value }));
            }}
            onKeyDown={(e) => { if (e.key === "Enter") endEpisode(); }}
            onBlur={endEpisode}
          />
        </div>
        {landingCollapsed ? <LandingHandle onOpen={() => setLandingCollapsed(false)} /> : null}
      </header>

      {landingCollapsed ? null : <LandingPanel onHide={() => setLandingCollapsed(true)} />}

      {/* A search landmark (#216): the filters, sort and search are how this
          list is queried, and outside any landmark a screen reader's
          region-by-region navigation skipped them. */}
      <div class="controls" role="search" aria-label="Choose which lenses to show">
        <CameraBar data={mountData} camera={camera} onChange={(c) => { announce("mount"); setCamera(c); }} />
        <SortControl registry={registry} query={query} onChange={setQuery} />

        {/* One row on a phone: three stacked 44px targets pushed the first
            result past the top third (REQ-WEB-019) once REQ-WEB-017's touch
            floor was met. Side by side satisfies both. */}
        <div class="controls-row">
        <Disclosure
          label="Filters"
          detail={filterDetail}
          open={panel === "filters"}
          onToggle={() => setPanel(panel === "filters" ? null : "filters")}
        >
          <FilterPanel
            registry={registry}
            ranges={ranges}
            mounts={mountData}
            scoped={scopedOptions}
            query={query}
            onChange={changeFilters}
            chosen={filterKeys}
            onChooseFilters={setFilterKeys}
          />
        </Disclosure>

        <Disclosure
          label="Specs shown"
          detail={`${query.columns.length} · max ${MAX_COLUMNS}`}
          open={panel === "columns"}
          onToggle={() => setPanel(panel === "columns" ? null : "columns")}
        >
          <ColumnPanel registry={registry} query={query} onChange={setQuery} max={MAX_COLUMNS} />
        </Disclosure>

        {/* Last, and named "More" (D26, #213): building a column is a rarer job
            than choosing what to filter or show, so it is tucked behind a
            button of its own that says only that there is more. Building one
            is still a thing you do before choosing which to show (REQ-FX-016),
            and the panel is headed "Calculated columns". */}
        <Disclosure
          label="More"
          detail={query.computed.length
            ? `${query.computed.length} ${query.computed.length === 1 ? "column" : "columns"}`
            : undefined}
          open={panel === "computed"}
          onToggle={() => setPanel(panel === "computed" ? null : "computed")}
        >
          <h2 class="panel-title">Calculated columns</h2>
          <ComputedPanel registry={registry} query={query} onChange={setQuery} />
        </Disclosure>
        </div>

        <ActiveFilterSummary registry={registry} query={query} onChange={changeFilters} />
      </div>

      <div class="resultbar" role="status" aria-live="polite">
        {error ? (
          <>
            <span class="resultbar-error">{error}</span>
            <button type="button" class="linkish" onClick={() => setAttempt((n) => n + 1)}>
              Try again
            </button>
          </>
        ) : loading ? (
          <span class="resultbar-count">Searching…</span>
        ) : (
          <span class="resultbar-count">{resultCount}</span>
        )}
        {query.filters.length ? (
          <button type="button" class="linkish" onClick={clearFilters}>
            Clear
          </button>
        ) : null}
        {!loading && !error && total ? (
          <button
            type="button"
            class="linkish resultbar-export"
            onClick={exportView}
            disabled={exporting}
          >
            {exporting ? "Preparing CSV…" : `Download all ${formatCount(total)} as CSV`}
          </button>
        ) : null}
        {exportError ? <span class="resultbar-error">{exportError}</span> : null}
      </div>

      {/* REQ-WEB-022: a link asked for more specs than a result shows. */}
      {columnsNotice ? (
        <p class="search-elsewhere" role="status">
          This link asked for {columnsNotice.asked} specs per result; a result shows at most{" "}
          {MAX_COLUMNS}, so the first {columnsNotice.shown} are shown.{" "}
          <button type="button" class="linkish" onClick={() => setColumnsNotice(null)}>
            OK
          </button>
        </p>
      ) : null}
      {ignoredNotice ? (
        <p class="search-elsewhere" role="status">
          Part of this link couldn’t be used and was left out: {ignoredNotice.join("; ")}.{" "}
          <button type="button" class="linkish" onClick={() => setIgnoredNotice(null)}>
            OK
          </button>
        </p>
      ) : null}
      {/* REQ-WEB-037: say what the camera and filters left out, and offer it. */}
      {!loading && !error && (pages[0]?.q_matches_elsewhere ?? 0) > 0 ? (
        <p class="search-elsewhere" role="status">
          {formatCount(pages[0]!.q_matches_elsewhere!)} more{" "}
          {pages[0]!.q_matches_elsewhere === 1 ? "lens matches" : "lenses match"} "{query.q.trim()}" on
          other mounts or outside your filters.{" "}
          <button
            type="button"
            class="linkish"
            onClick={() => {
              // One change, not two: it is its own entry (D33).
              announce("mount");
              setCamera(Cam.ALL);
              setQuery((cur) => ({ ...cur, filters: [] }));
              setFilterKeys([]);
            }}
          >
            Show them
          </button>
        </p>
      ) : null}


      <main class={loading ? "results is-loading" : "results"}>
        {rows.length === 0 && !loading && !error ? (
          <EmptyResult hasFilters={query.filters.length > 0} onClear={clearFilters} />
        ) : wide ? (
          <ResultTable
            rows={rows}
            query={query}
            byKey={byKey}
            ranges={ranges}
            onOpen={openLens}
            selected={selectedSet}
            onToggleSelect={toggleSelect}
            cropsBelow={cropsBelow}
            mountOf={mountOf}
            lensSearch={lensSearch}
          />
        ) : (
          <ResultCards
            rows={rows}
            query={query}
            byKey={byKey}
            ranges={ranges}
            onOpen={openLens}
            selected={selectedSet}
            onToggleSelect={toggleSelect}
            cropsBelow={cropsBelow}
            mountOf={mountOf}
            lensSearch={lensSearch}
          />
        )}

        {/* REQ-WEB-034 (#205): a filter never silently drops a lens for missing
            data. The visitor lost Sony's own FE 24mm F2.8 G from "a light
            wide-angle lens for Sony" because its focal length was never
            recorded. Below the results, not above them (B1, 2026-09-29 QA
            pass, #286): above the list this two-line notice pushed the first
            card past REQ-WEB-019's top-third limit whenever a filter excluded
            anything; REQ-WEB-034 says the count must be stated, not where. */}
        {!loading && !error && (pages[0]?.missing_data_excluded ?? 0) > 0 ? (
          <p class="search-elsewhere" role="status">
            {formatCount(pages[0]!.missing_data_excluded!)} more{" "}
            {pages[0]!.missing_data_excluded === 1 ? "lens was" : "lenses were"} left out because a
            filtered spec isn't recorded for {pages[0]!.missing_data_excluded === 1 ? "it" : "them"} yet.
          </p>
        ) : null}

        {/* REQ-WEB-042: sorting by release date puts the lenses with no cited
            date last, in both directions, and says how many they are, the way
            REQ-WEB-034 does for a filter. Below the list for the same reason. */}
        {!loading && !error && (pages[0]?.undated ?? 0) > 0 ? (
          <p class="search-elsewhere" role="status">
            {formatCount(pages[0]!.undated!)} {pages[0]!.undated === 1 ? "lens has" : "lenses have"} no
            release date on record yet, so {pages[0]!.undated === 1 ? "it is" : "they are"} listed last.
          </p>
        ) : null}

        <SmallerCoverageOptIn data={mountData} camera={camera} onChange={setCamera} />

        {lastPage?.has_more ? (
          <div class="more">
            <button
              type="button"
              class="button more-button"
              ref={moreRef}
              onClick={loadMore}
              disabled={loadingMore}
            >
              {loadingMore ? "Loading…" : `Load ${formatCount(Math.min(PAGE_SIZE, remaining))} more`}
            </button>
            <p class="more-note">
              {moreError ?? `${formatCount(remaining)} more ${remaining === 1 ? "lens" : "lenses"} to load`}
            </p>
          </div>
        ) : null}
      </main>

      {/* After the list, not before it: a sticky bar only sticks while its own
          place in the page is below the viewport. Placed above the results it
          scrolled off the top as soon as anyone scrolled into them, so the
          selection count REQ-WEB-027 requires "while browsing" was invisible
          exactly while browsing (#200, measured at -469 px on a phone). */}
      {selected.length ? (
        <div class="selectionbar">
          <span class="selectionbar-count">
            {selected.length} selected
            {selected.length < 2 ? ". Tick one more to compare." : ""}
          </span>
          <button type="button" class="linkish" onClick={() => setSelected([])}>
            Clear
          </button>
          <button
            type="button"
            class="button button-small"
            disabled={selected.length < 2}
            onClick={() => navigate(`/compare?${search}`)}
          >
            Compare
          </button>
        </div>
      ) : null}

      <footer class="footer">
        {freshness ? <p class="footer-meta" id="freshness">{freshness}</p> : null}
        <LegendNote dash />
        <Disclaimer />
        <SiteLinks />
        {/* No build identifier here: the list, lens and compare pages show no
            text about the implementation (REQ-WEB-030). It moved to the
            bottom of the About page (D32). */}
      </footer>
    </div>
  );
}
